import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createLeaveRequest, decideLeaveRequest, listEmployees, listLeaveRequests } from '../../api/hr';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Pagination,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDate,
  formatDateTime,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { Employee, LeaveRequest, LeaveRequestStatus } from '../../types/domain';

const LEAVE_STATUSES: LeaveRequestStatus[] = ['REQUESTED', 'APPROVED', 'REJECTED', 'CANCELLED'];
const LEAVE_TYPE_LABEL: Record<LeaveRequest['leave_type'], string> = {
  SICK: 'Sakit',
  ANNUAL: 'Tahunan',
  REQUEST_OFF: 'Izin',
  OTHER: 'Lainnya',
};

/**
 * Leave workspace (FASE 9, migration 0040). Branch-scoped — re-fetches on
 * Cabang Aktif change. date_to >= date_from is validated both client-side
 * and by fn_create_leave_request itself (server is authoritative); no
 * leave-balance rule is shown or enforced — no authoritative leave-balance
 * policy exists in this schema.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md NEEDS_DECISION log.
 */
export default function LeavePage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selected, setSelected] = useState<LeaveRequest | null>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canCreate = hasPermission('leave_request', 'create');
  const canApprove = hasPermission('leave_request', 'approve');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [leaveRows, empRows] = await Promise.all([listLeaveRequests(activeBranchId ?? undefined), listEmployees(activeBranchId ?? undefined)]);
      setLeaves(leaveRows);
      setEmployees(empRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const employeeName = (id: string) => employees.find((e) => e.employee_id === id)?.legal_name ?? id;

  const handleDecide = async (leaveId: string, approve: boolean) => {
    setActionError(null);
    setDecidingId(leaveId);
    try {
      await decideLeaveRequest(leaveId, approve);
      await load();
      setSelected(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setDecidingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leaves.filter((l) => {
      if (q && !employeeName(l.employee_id).toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && l.status !== statusFilter) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaves, search, statusFilter, employees]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader title="Cuti" subtitle="Pengajuan dan persetujuan cuti karyawan. Migrasi 0040." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Ajukan Cuti'}
        </Button>
      )}
      {showForm && canCreate && (
        <NewLeaveForm
          employees={employees}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {actionError && <ErrorNote text={actionError} />}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={(() => {
              const c = statusCounts(leaves, LEAVE_STATUSES);
              return [
                { label: 'Total', value: leaves.length },
                { label: 'Pending', value: c.REQUESTED, tone: 'warning' as const },
                { label: 'Disetujui', value: c.APPROVED, tone: 'success' as const },
                { label: 'Ditolak', value: c.REJECTED, tone: 'danger' as const },
              ];
            })()}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari nama karyawan..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="REQUESTED">Pending</option>
                <option value="APPROVED">Disetujui</option>
                <option value="REJECTED">Ditolak</option>
                <option value="CANCELLED">Dibatalkan</option>
              </Select>
            }
          />
          {leaves.length === 0 && <EmptyState text="Belum ada pengajuan cuti." />}
          {leaves.length > 0 && total === 0 && <EmptyState text="Tidak ada cuti yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Karyawan</th>
                  <th style={thStyle}>Jenis</th>
                  <th style={thStyle}>Tanggal</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((l) => (
                  <ClickableRow key={l.leave_id} onClick={() => setSelected(l)}>
                    <td style={tdStyle}>{employeeName(l.employee_id)}</td>
                    <td style={tdStyle}>{LEAVE_TYPE_LABEL[l.leave_type]}</td>
                    <td style={tdStyle}>{formatDate(l.date_from)} – {formatDate(l.date_to)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={l.status} />
                    </td>
                    <td style={tdStyle}>
                      {canApprove && l.status === 'REQUESTED' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button
                            variant="primary"
                            loading={decidingId === l.leave_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDecide(l.leave_id, true);
                            }}
                          >
                            Setujui
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDecide(l.leave_id, false);
                            }}
                          >
                            Tolak
                          </Button>
                        </div>
                      )}
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected ? employeeName(selected.employee_id) : 'Detail Cuti'}>
        {selected && (
          <div>
            <DrawerField label="Jenis" value={LEAVE_TYPE_LABEL[selected.leave_type]} />
            <DrawerField label="Tanggal" value={`${formatDate(selected.date_from)} – ${formatDate(selected.date_to)}`} />
            <DrawerField label="Status" value={<StatusBadge status={selected.status} />} />
            <DrawerField label="Alasan" value={selected.reason ?? '—'} />
            <DrawerField label="Diajukan" value={formatDateTime(selected.created_at)} />
            {selected.decided_at && <DrawerField label="Diputuskan" value={formatDateTime(selected.decided_at)} />}
            {canApprove && selected.status === 'REQUESTED' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button variant="primary" loading={decidingId === selected.leave_id} onClick={() => void handleDecide(selected.leave_id, true)}>
                  Setujui
                </Button>
                <Button variant="ghost" onClick={() => void handleDecide(selected.leave_id, false)}>
                  Tolak
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

function NewLeaveForm({ employees, onCreated }: { employees: Employee[]; onCreated: () => void }) {
  const [employeeId, setEmployeeId] = useState('');
  const [leaveType, setLeaveType] = useState<LeaveRequest['leave_type']>('ANNUAL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (dateTo < dateFrom) {
      setError('Tanggal selesai harus >= tanggal mulai.');
      return;
    }
    setSubmitting(true);
    try {
      await createLeaveRequest(employeeId, leaveType, dateFrom, dateTo, reason || null);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Karyawan">
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {employees.map((emp) => (
              <option key={emp.employee_id} value={emp.employee_id}>
                {emp.employee_no} — {emp.legal_name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 140 }}>
        <FieldLabel label="Jenis">
          <Select value={leaveType} onChange={(e) => setLeaveType(e.target.value as LeaveRequest['leave_type'])}>
            <option value="ANNUAL">Tahunan</option>
            <option value="SICK">Sakit</option>
            <option value="REQUEST_OFF">Izin</option>
            <option value="OTHER">Lainnya</option>
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Dari">
          <TextInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Sampai">
          <TextInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 160 }}>
        <FieldLabel label="Alasan (opsional)">
          <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Ajukan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
